/**
 * Content-domain contract checks — TOML data pipeline, hotbars, equipment,
 * loot, combat feedback, mob rendering. These read static content tables
 * and the client-side MobManager registry.
 */

import type { Check } from '../../DashboardShared';
import type { Category } from '../types';
import { ServiceRegistry } from '../../../../core/ServiceRegistry';
import { EventBus } from '../../../../../shared/EventBus';

// Data modules
import { ITEM_DATABASE, MONSTER_DROPS, GOLD_DROP_CONFIG } from '../../../../../shared/ItemData';
import { MOB_REGISTRY } from '../../../../../shared/MobRegistry';
import { NPC_DATABASE } from '../../../../../shared/NPCData';
import { QUEST_DATABASE } from '../../../../../shared/QuestData';
import { RECIPE_DATABASE } from '../../../../../shared/data/RecipeDatabase';
import { DEFAULT_HOTBAR_SLOTS, UNLOCKABLE_ABILITIES } from '../../../../../shared/constants/DefaultHotbars';
import { MOB_CONFIG, NPC_CONFIG } from '../../../../../shared/constants/EntityConfig';
import { EQUIPMENT_SETS } from '../../../../../shared/content/generated/items.gen';
import { DAMAGE_TYPE_COLORS, IMPACT_INTENSITY } from '../../../../../shared/constants/CombatFeedbackConfig';
import { CombatFeedbackManager } from '../../../../effects/CombatFeedbackManager';

export function checkTomlPipeline(): Category {
  const checks: Check[] = [];

  const itemCount = Object.keys(ITEM_DATABASE).length;
  checks.push({
    id: 'items_loaded', label: 'ITEM_DATABASE',
    status: itemCount > 0 ? 'pass' : 'fail',
    detail: `${itemCount} items loaded`,
  });

  const mobCount = Object.keys(MOB_REGISTRY).length;
  checks.push({
    id: 'mobs_loaded', label: 'MOB_REGISTRY',
    status: mobCount > 0 ? 'pass' : 'fail',
    detail: `${mobCount} mobs loaded`,
  });

  const npcCount = Object.keys(NPC_DATABASE).length;
  checks.push({
    id: 'npcs_loaded', label: 'NPC_DATABASE',
    status: npcCount > 0 ? 'pass' : 'fail',
    detail: `${npcCount} NPCs loaded`,
  });

  const questCount = Object.keys(QUEST_DATABASE).length;
  checks.push({
    id: 'quests_loaded', label: 'QUEST_DATABASE',
    status: questCount > 0 ? 'pass' : 'fail',
    detail: `${questCount} quests loaded`,
  });

  const recipeCount = RECIPE_DATABASE instanceof Map ? RECIPE_DATABASE.size : Object.keys(RECIPE_DATABASE).length;
  checks.push({
    id: 'recipes_loaded', label: 'RECIPE_DATABASE',
    status: recipeCount > 0 ? 'pass' : 'fail',
    detail: `${recipeCount} recipes loaded`,
  });

  const mobConfigCount = Object.keys(MOB_CONFIG).length;
  checks.push({
    id: 'mob_config_barrel', label: 'MOB_CONFIG barrel',
    status: mobConfigCount > 0 ? 'pass' : 'fail',
    detail: `${mobConfigCount} mob configs via EntityConfig`,
  });

  const npcConfigCount = Object.keys(NPC_CONFIG).length;
  checks.push({
    id: 'npc_config_barrel', label: 'NPC_CONFIG barrel',
    status: npcConfigCount > 0 ? 'pass' : 'fail',
    detail: `${npcConfigCount} NPC configs via EntityConfig`,
  });

  return { id: 'toml-pipeline', label: 'TOML Pipeline', icon: '\uD83D\uDCE6', checks };
}

export function checkHotbarContracts(): Category {
  const checks: Check[] = [];

  const classes = ['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid'];
  for (const cls of classes) {
    const hotbar = DEFAULT_HOTBAR_SLOTS[cls];
    checks.push({
      id: `hotbar_${cls}`, label: `${cls} hotbar`,
      status: hotbar && hotbar.length > 0 ? 'pass' : 'fail',
      detail: hotbar ? `${hotbar.length} slots` : 'MISSING',
    });
  }

  const unlockCount = Object.keys(UNLOCKABLE_ABILITIES).length;
  checks.push({
    id: 'unlockable_abilities', label: 'UNLOCKABLE_ABILITIES',
    status: unlockCount > 0 ? 'pass' : 'fail',
    detail: `${unlockCount} class entries`,
  });

  return { id: 'hotbars', label: 'Hotbar Contracts', icon: '\uD83C\uDFAE', checks };
}

export function checkEquipmentContracts(): Category {
  const checks: Check[] = [];

  const equipItems = Object.values(ITEM_DATABASE).filter(
    (item: any) => item.type === 'weapon' || item.type === 'armor'
  );
  checks.push({
    id: 'equip_count', label: 'Equippable items in ITEM_DATABASE',
    status: equipItems.length > 0 ? 'pass' : 'fail',
    detail: `${equipItems.length} items (${equipItems.filter((i: any) => i.type === 'weapon').length} weapons, ${equipItems.filter((i: any) => i.type === 'armor').length} armor)`,
  });

  const VALID_SLOTS = ['weapon', 'hat', 'top', 'bottom', 'shoes', 'gloves', 'cape', 'accessory', 'lantern', 'flame'];
  const coveredSlots = new Set(equipItems.map((i: any) => i.equipmentSlot).filter(Boolean));
  const missingSlots = VALID_SLOTS.filter(s => !coveredSlots.has(s));
  checks.push({
    id: 'equip_slots', label: 'Equipment slot coverage',
    status: missingSlots.length === 0 ? 'pass' : missingSlots.length <= 3 ? 'warn' : 'fail',
    detail: missingSlots.length === 0
      ? `All ${VALID_SLOTS.length} slots have items`
      : `Missing: ${missingSlots.join(', ')}`,
  });

  const noStats = equipItems.filter((i: any) => !i.stats || Object.keys(i.stats).length === 0);
  checks.push({
    id: 'equip_stats', label: 'Equipment stat integrity',
    status: noStats.length === 0 ? 'pass' : 'warn',
    detail: noStats.length === 0
      ? `All ${equipItems.length} items have stats`
      : `${noStats.length} items missing stats: ${noStats.map((i: any) => i.id).join(', ')}`,
  });

  const SLOT_MAP: Record<string, string> = {
    weapon: 'mainHand', hat: 'head', top: 'chest', bottom: 'legs',
    shoes: 'feet', gloves: 'hands', cape: 'back', accessory: 'neck', lantern: 'charm', flame: 'halo',
  };
  const unmapped = equipItems.filter((i: any) => i.equipmentSlot && !SLOT_MAP[i.equipmentSlot]);
  checks.push({
    id: 'equip_mapping', label: 'Equip slot → schema mapping',
    status: unmapped.length === 0 ? 'pass' : 'fail',
    detail: unmapped.length === 0
      ? `All slots map to schema fields`
      : `Unmapped: ${unmapped.map((i: any) => `${i.id}:${i.equipmentSlot}`).join(', ')}`,
  });

  const setNames = Object.keys(EQUIPMENT_SETS);
  const brokenSets = setNames.filter(name =>
    EQUIPMENT_SETS[name].some((itemId: string) => !ITEM_DATABASE[itemId])
  );
  checks.push({
    id: 'equip_sets', label: 'Class starter gear sets',
    status: brokenSets.length === 0 ? 'pass' : 'fail',
    detail: brokenSets.length === 0
      ? `${setNames.length} sets valid (${setNames.join(', ')})`
      : `Broken sets: ${brokenSets.join(', ')}`,
  });

  return { id: 'equipment', label: 'Equipment Contracts', icon: '⚔️', checks };
}

export function checkLootContracts(): Category {
  const checks: Check[] = [];

  const dropCount = Object.keys(MONSTER_DROPS).length;
  checks.push({
    id: 'loot_drops', label: 'MONSTER_DROPS loaded',
    status: dropCount >= 5 ? 'pass' : dropCount > 0 ? 'warn' : 'fail',
    detail: `${dropCount} mob types with drop tables`,
  });

  const goldCount = Object.keys(GOLD_DROP_CONFIG).length;
  checks.push({
    id: 'loot_gold', label: 'GOLD_DROP_CONFIG loaded',
    status: goldCount >= 5 ? 'pass' : goldCount > 0 ? 'warn' : 'fail',
    detail: `${goldCount} mob gold configs`,
  });

  const invalidRefs: string[] = [];
  for (const [mob, table] of Object.entries(MONSTER_DROPS)) {
    for (const tier of ['common', 'uncommon', 'rare'] as const) {
      const drops = (table as any)[tier];
      if (!drops) continue;
      for (const drop of drops) {
        if (!ITEM_DATABASE[drop.itemId]) {
          invalidRefs.push(`${mob}.${tier}: ${drop.itemId}`);
        }
      }
    }
  }
  checks.push({
    id: 'loot_refs', label: 'Drop item refs valid',
    status: invalidRefs.length === 0 ? 'pass' : 'fail',
    detail: invalidRefs.length === 0
      ? 'All item references resolve'
      : `${invalidRefs.length} broken: ${invalidRefs.slice(0, 3).join(', ')}`,
  });

  let invalidRates = 0;
  for (const table of Object.values(MONSTER_DROPS)) {
    for (const tier of ['common', 'uncommon', 'rare'] as const) {
      const drops = (table as any)[tier];
      if (!drops) continue;
      for (const drop of drops) {
        if (drop.dropRate < 0 || drop.dropRate > 1) invalidRates++;
      }
    }
  }
  checks.push({
    id: 'loot_rates', label: 'Drop rates valid',
    status: invalidRates === 0 ? 'pass' : 'fail',
    detail: invalidRates === 0 ? 'All rates in [0, 1]' : `${invalidRates} invalid rates`,
  });

  let invalidGold = 0;
  for (const config of Object.values(GOLD_DROP_CONFIG)) {
    if (config.min > config.max || config.chance < 0 || config.chance > 1) invalidGold++;
  }
  checks.push({
    id: 'loot_gold_range', label: 'Gold ranges valid',
    status: invalidGold === 0 ? 'pass' : 'fail',
    detail: invalidGold === 0 ? 'All min ≤ max, chance in [0,1]' : `${invalidGold} invalid configs`,
  });

  return { id: 'loot', label: 'Loot System Contracts', icon: '💎', checks };
}

export function checkCombatFeedbackContracts(): Category {
  const checks: Check[] = [];

  const expectedSchools = ['physical', 'fire', 'frost', 'arcane', 'shadow', 'holy', 'nature', 'chaos', 'various'];
  const missingColors = expectedSchools.filter(s => !DAMAGE_TYPE_COLORS[s]);
  checks.push({
    id: 'cfb_colors', label: 'DAMAGE_TYPE_COLORS',
    status: missingColors.length === 0 ? 'pass' : 'warn',
    detail: missingColors.length === 0
      ? `All ${expectedSchools.length} schools have colors`
      : `Missing: ${missingColors.join(', ')}`,
  });

  const ordered = IMPACT_INTENSITY.light < IMPACT_INTENSITY.medium && IMPACT_INTENSITY.medium < IMPACT_INTENSITY.heavy;
  checks.push({
    id: 'cfb_intensity', label: 'IMPACT_INTENSITY thresholds',
    status: ordered ? 'pass' : 'fail',
    detail: ordered
      ? `light(${IMPACT_INTENSITY.light}) < medium(${IMPACT_INTENSITY.medium}) < heavy(${IMPACT_INTENSITY.heavy})`
      : 'Thresholds not in ascending order',
  });

  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem') || registry.has('PhysicsSystem');
  if (isGameSceneLoaded) {
    const cfm = CombatFeedbackManager.getInstance();
    const cfmInitialized = (cfm as any).scene != null && (cfm as any).vfxSystem != null;
    checks.push({
      id: 'cfb_init', label: 'CombatFeedbackManager initialized',
      status: cfmInitialized ? 'pass' : 'fail',
      detail: cfmInitialized ? 'scene + vfxSystem bound' : 'BROKEN — init() never called, all feedback silent',
    });

    const bus = EventBus.getInstance();
    const meleeListeners = bus.listenerCount('combat:melee_hit');
    checks.push({
      id: 'cfb_bridge', label: 'DamageBridge melee_hit',
      status: meleeListeners > 0 ? 'pass' : 'warn',
      detail: meleeListeners > 0 ? `${meleeListeners} listener(s)` : 'No listeners',
    });
  } else {
    checks.push({
      id: 'cfb_runtime', label: 'Runtime feedback checks',
      status: 'pass',
      detail: 'Game scene not loaded — deferred',
    });
  }

  return { id: 'combat-feedback', label: 'Combat Feedback Contracts', icon: '💥', checks };
}

export function checkMobRendering(): Category {
  const checks: Check[] = [];
  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem');

  if (!isGameSceneLoaded) {
    checks.push({ id: 'mob_deferred', label: 'Mob rendering checks', status: 'pass', detail: 'Game scene not loaded — deferred' });
    return { id: 'mob-rendering', label: 'Mob Rendering', icon: '🐷', checks };
  }

  const mobManager = registry.get('MobSystem', { optional: true })?.getManager();
  if (!mobManager) {
    checks.push({ id: 'mob_manager', label: 'MobManager service', status: 'fail', detail: 'MISSING — mobs cannot spawn on client' });
    return { id: 'mob-rendering', label: 'Mob Rendering', icon: '🐷', checks };
  }
  checks.push({ id: 'mob_manager', label: 'MobManager service', status: 'pass', detail: 'registered' });

  const allMobs = mobManager.getAllMobs?.() || [];
  checks.push({
    id: 'mob_count', label: 'Active mobs',
    status: allMobs.length > 0 ? 'pass' : 'warn',
    detail: `${allMobs.length} mob(s) on client`,
  });

  let withSprite = 0;
  let withoutSprite = 0;
  const missingSpriteMobs: string[] = [];
  for (const mob of allMobs) {
    if (mob.hasBodySprite?.()) {
      withSprite++;
    } else {
      withoutSprite++;
      missingSpriteMobs.push(`${mob.enemyName || mob.id} (${mob.type})`);
    }
  }

  if (allMobs.length > 0) {
    checks.push({
      id: 'mob_sprites', label: 'Body sprites loaded',
      status: withoutSprite === 0 ? 'pass' : 'fail',
      detail: withoutSprite === 0
        ? `${withSprite}/${allMobs.length} have sprites`
        : `MISSING sprites: ${missingSpriteMobs.join(', ')}`,
    });
  }

  let withAI = 0;
  let stuck = 0;
  for (const mob of allMobs) {
    const aiState = (mob as any)._aiState;
    if (aiState && aiState !== 'dead') {
      withAI++;
    } else if (!aiState) {
      stuck++;
    }
  }

  if (allMobs.length > 0) {
    checks.push({
      id: 'mob_ai', label: 'AI state synced',
      status: stuck === 0 ? 'pass' : 'warn',
      detail: stuck === 0
        ? `${withAI}/${allMobs.length} have AI state`
        : `${stuck} mob(s) have no aiState — server may not be syncing`,
    });
  }

  const mobTypes = new Set(allMobs.map((m: any) => m.type));
  const knownTypes = ['pig', 'snail', 'slime', 'mushroom', 'training_dummy', 'wolf', 'bat', 'cave_spider', 'crystal_golem'];
  const unknownTypes = [...mobTypes].filter(t => !knownTypes.includes(t as string));
  checks.push({
    id: 'mob_svg_types', label: 'SVG generators for mob types',
    status: unknownTypes.length === 0 ? 'pass' : 'warn',
    detail: unknownTypes.length === 0
      ? `All ${mobTypes.size} type(s) have SVG generators`
      : `Unknown types (fallback pig): ${unknownTypes.join(', ')}`,
  });

  return { id: 'mob-rendering', label: 'Mob Rendering', icon: '🐷', checks };
}
